import { extractApiErrorCode } from '@hub/lib/api-error/extract-api-error-code';
import { ApiError, passwordResetVerify } from '@platform/api';
import type { PasswordResetErrorCode } from './errorCodes';

export type ResetPasswordVerification = { status: 'valid' | 'invalid-link' };

/**
 * A missing or dead token is the expected outcome of an emailed link, and the route's error boundary
 * reports every throw to Sentry — so it comes back as data instead. Transport failures do throw: the
 * user needs a retry, not the dead end of being told to request a link that would fail too. Verify
 * has no side effect, so the error page's retry is safe to offer.
 */
export async function loadPasswordResetVerification(token: string): Promise<ResetPasswordVerification> {
  if (!token) {
    return { status: 'invalid-link' };
  }

  try {
    await passwordResetVerify({ body: { token }, throwOnError: true });
    return { status: 'valid' };
  } catch (error) {
    if (!(error instanceof ApiError) || error.status >= 500) {
      throw error;
    }
    extractApiErrorCode(error, { flow: 'password_reset_verify', byCode: new Set<PasswordResetErrorCode>(['link-expired']) });
    return { status: 'invalid-link' };
  }
}
